import { useMemo, useCallback, useState } from 'react';
import { Modal, InlineNotification } from '@carbon/react';
import { CollapsibleFilterPanel } from 'components/collapsible-filter-panel';
import { EntityFilters } from 'pages/entities/list/entity-filters';
import { EntityTable } from 'pages/entities/list/entity-table';
import type { EntitySearchQueryResult } from 'types/api-responses';
import { EntityRowAction } from './entity-row-action';
import { useEntitySearchHandlers } from './hooks';
import type { EntitySearchState } from './types';
import { countEntityFilters } from './utils';
import styles from './entity-search-modal.module.scss';

interface EntitySearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  // The caller's entity search, with the types it is locked to.
  search: EntitySearchState;
  selectedEntityIds: number[];
  primaryEntityId?: number;
  onSelect: (entities: EntitySearchQueryResult[]) => void;
}

/**
 * Searches entities to add to a reconciliation, collecting picks until
 * Continue hands them over.
 * @param {EntitySearchModalProps} props The modal.
 * @return {JSX.Element} The modal.
 */
export function EntitySearchModal({
  isOpen,
  onClose,
  search,
  selectedEntityIds,
  primaryEntityId,
  onSelect,
}: EntitySearchModalProps) {
  const { lockedEntityTypes } = search;
  const typeFilterLocked = lockedEntityTypes !== null;

  // Plain-language note for the locked filter (the canonical vs. Imported case).
  const lockedNoteText = useMemo(() => {
    if (!lockedEntityTypes) return '';
    const canonical = lockedEntityTypes.find((type) => type !== 'Imported');
    if (!canonical) {
      return 'When deduplicating from an Imported entity, only other Imported entities can be selected as duplicates.';
    }
    return `The reconciliation's surviving entity is an ${canonical}. Only ${canonical} and Imported entities can be added.`;
  }, [lockedEntityTypes]);

  const { result: searchResult, setPayload } = search;
  const handlers = useEntitySearchHandlers(setPayload, lockedEntityTypes);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [pendingSelections, setPendingSelections] = useState<
    EntitySearchQueryResult[]
  >([]);

  const payload = searchResult.currentPayload;
  const filterCount = useMemo(
    () => countEntityFilters(payload, typeFilterLocked),
    [payload, typeFilterLocked]
  );

  const pendingSelectionIds = useMemo(
    () => pendingSelections.map((e) => e.id),
    [pendingSelections]
  );

  const handleCancel = useCallback(() => {
    setPendingSelections([]);
    onClose();
  }, [onClose]);

  const handleContinue = useCallback(() => {
    if (pendingSelections.length > 0) {
      onSelect(pendingSelections);
    }
    setPendingSelections([]);
    onClose();
  }, [pendingSelections, onSelect, onClose]);

  const renderRowAction = useCallback(
    (entity: EntitySearchQueryResult) => (
      <EntityRowAction
        isPending={pendingSelectionIds.includes(entity.id)}
        disabled={
          selectedEntityIds.includes(entity.id) || entity.id === primaryEntityId
        }
        onAdd={() => setPendingSelections((prev) => [...prev, entity])}
        onRemove={() =>
          setPendingSelections((prev) => prev.filter((e) => e.id !== entity.id))
        }
      />
    ),
    [selectedEntityIds, primaryEntityId, pendingSelectionIds]
  );

  return (
    <Modal
      open={isOpen}
      size="lg"
      className={styles.entitySearchModal}
      modalHeading="Search Entities"
      primaryButtonText="Continue"
      secondaryButtonText="Cancel"
      onRequestSubmit={handleContinue}
      onSecondarySubmit={handleCancel}
      onRequestClose={handleCancel}
    >
      {isOpen && (
        <div className={styles.modalContent}>
          {typeFilterLocked && (
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title=""
              subtitle={lockedNoteText}
              className={styles.importedNotification}
            />
          )}
          <div className={styles.layoutWrapper}>
            <CollapsibleFilterPanel
              filterCount={filterCount}
              onReset={handlers.onReset}
              isOpen={isFilterOpen}
            >
              <EntityFilters
                payload={payload}
                onCreatedAtChange={handlers.onCreatedAtChange}
                onUpdatedAtChange={handlers.onUpdatedAtChange}
                onEntityTypesChange={handlers.onEntityTypesChange}
                onEntityTypeFilterChange={handlers.onEntityTypeFilterChange}
                disableEntityTypeFilter={typeFilterLocked}
                disableDeduplicatedFilter
              />
            </CollapsibleFilterPanel>
            <div className={styles.tableWrapper}>
              <EntityTable
                searchResult={searchResult}
                isFilterOpen={isFilterOpen}
                onToggleFilter={() => setIsFilterOpen(!isFilterOpen)}
                filterCount={filterCount}
                onSearchChange={handlers.onSearchChange}
                onSortChange={handlers.onSortChange}
                onPaginationChange={handlers.onPaginationChange}
                tableId="entity-search-modal-table"
                renderRowAction={renderRowAction}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
